import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { BookButton, BookModal, BookTexField, ImageUploader } from '../../components';
import { AnnouncementValidation } from '../Announcements/validation';
import { CreateAnnouncement, UpdateAnnouncement } from '../../DAL/Announcements/announcements';
import { GetBooks, GetBooksByLanguage } from '../../DAL/Books/books';
import { GetBookChapters } from '../../DAL/BooksChapters/bookChapters';
import { GetLanguage } from '../../DAL/Language/language';
import { enqueueSnackbar } from 'notistack';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const formatBookOption = (item) => ({
  value: item.id,
  text:
    item.bookNo != null && item.bookNo !== ''
      ? `${item.bookName} (Book No: ${item.bookNo})`
      : item.bookName,
});

const formatChapterOption = (item) => ({
  value: item.id,
  text:
    item.chapterNo != null && item.chapterNo !== ''
      ? `${item.chapterName} (Chapter No: ${item.chapterNo})`
      : item.chapterName,
});

const buildPayload = (values) => {
  const common = {
    title: values.title,
    description: values.description,
    link: values.link || null,
    scheduledAt: values.scheduledAt ? dayjs(values.scheduledAt).toISOString() : null,
    url: values.url || null,
  };

  if (values.isInternal) {
    return {
      ...common,
      isExternal: false,
      bookId: values.bookId,
      chapterId: values.chapterId,
      pageNo: Number(values.pageNo),
      searchQuery: values.searchQuery || null,
    };
  }

  return {
    ...common,
    isExternal: true,
    bookId: null,
    chapterId: null,
    pageNo: null,
    searchQuery: null,
  };
};

const AnnouncementModals = ({
  open,
  setOpen,
  action,
  singleAnnouncement,
  handleGetAnnouncementData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [bookOptions, setBookOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [filterLanguageId, setFilterLanguageId] = useState('');

  const isEdit = action !== 'add' && singleAnnouncement;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: isEdit ? singleAnnouncement.id : '',
      isInternal: isEdit ? singleAnnouncement.isExternal === false : false,
      title: isEdit ? singleAnnouncement.title || '' : '',
      description: isEdit ? singleAnnouncement.description || '' : '',
      link: isEdit ? singleAnnouncement.link || '' : '',
      scheduledAt:
        isEdit && singleAnnouncement.scheduledAt
          ? dayjs(singleAnnouncement.scheduledAt)
          : null,
      url: isEdit ? singleAnnouncement.url || '' : '',
      bookId: isEdit
        ? singleAnnouncement.bookIdRef || singleAnnouncement.bookId || ''
        : '',
      chapterId: isEdit
        ? singleAnnouncement.chapterIdRef || singleAnnouncement.chapterId || ''
        : '',
      pageNo: isEdit && singleAnnouncement.pageNo != null ? singleAnnouncement.pageNo : '',
      searchQuery: isEdit ? singleAnnouncement.searchQuery || '' : '',
    },
    validationSchema: AnnouncementValidation,
    onSubmit: (values) => {
      const data = buildPayload(values);
      if (action === 'add') {
        handleAddAnnouncement(data);
      } else {
        handleUpdateAnnouncement(values.id, data);
      }
    },
  });

  useEffect(() => {
    if (!open) {
      setFilterLanguageId('');
      setBookOptions([]);
      return;
    }

    const loadLanguages = async () => {
      const response = await GetLanguage(false);
      if (response.statusCode == 200) {
        setLanguageOptions(
          response.data.rows.map((item) => ({
            value: item.id,
            text: item.name,
          }))
        );
      }
    };
    loadLanguages();

    if (isEdit && (singleAnnouncement.bookIdRef || singleAnnouncement.bookId)) {
      const bookId = singleAnnouncement.bookIdRef || singleAnnouncement.bookId;
      const initLanguageFilter = async () => {
        const response = await GetBooks(false);
        if (response.statusCode == 200) {
          const book = response.data.rows.find((item) => item.id === bookId);
          if (book?.languageId) {
            setFilterLanguageId(book.languageId);
          }
        }
      };
      initLanguageFilter();
    }
  }, [open, isEdit, singleAnnouncement]);

  useEffect(() => {
    if (!open || !formik.values.isInternal) {
      return;
    }

    if (!filterLanguageId) {
      setBookOptions([]);
      return;
    }

    const loadBooks = async () => {
      const response = await GetBooksByLanguage({
        page: 1,
        limit: 1000,
        languageId: filterLanguageId,
      });
      if (response.statusCode == 200) {
        setBookOptions(response.data.rows.map(formatBookOption));
      }
    };
    loadBooks();
  }, [open, filterLanguageId, formik.values.isInternal]);

  useEffect(() => {
    if (!formik.values.bookId) {
      setChapterOptions([]);
      return;
    }
    const loadChapters = async () => {
      const response = await GetBookChapters({ bookId: formik.values.bookId });
      if (response.statusCode == 200) {
        setChapterOptions(response.data.rows.map(formatChapterOption));
      }
    };
    loadChapters();
  }, [formik.values.bookId]);

  const handleAddAnnouncement = async (data) => {
    setIsLoading(true);
    const response = await CreateAnnouncement(data);
    setIsLoading(false);
    if (response.statusCode == 200) {
      handleGetAnnouncementData();
      setOpen(false);
      formik.handleReset();
      enqueueSnackbar('Announcement created successfully!', { variant: 'success' });
    } else if (typeof response.message == 'string') {
      enqueueSnackbar(response.message, { variant: 'error' });
    } else {
      enqueueSnackbar('Invalid credentials', { variant: 'error' });
    }
  };

  const handleUpdateAnnouncement = async (id, data) => {
    setIsLoading(true);
    const response = await UpdateAnnouncement(id, data);
    setIsLoading(false);
    if (response.statusCode == 200) {
      handleGetAnnouncementData();
      setOpen(false);
      formik.handleReset();
      enqueueSnackbar('Announcement updated successfully!', { variant: 'success' });
    } else if (typeof response.message == 'string') {
      enqueueSnackbar(response.message, { variant: 'error' });
    } else {
      enqueueSnackbar('Invalid credentials', { variant: 'error' });
    }
  };

  return (
    <BookModal
      open={open}
      formik={formik}
      setOpen={setOpen}
      title='Announcement'
      subtitle={`${action === 'add' ? 'Add' : 'Edit'} Announcement`}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={formik.values.isInternal}
            onChange={(e) => formik.setFieldValue('isInternal', e.target.checked)}
          />
        }
        label='Internal'
        sx={{ mb: 1 }}
      />

      {formik.values.isInternal && (
        <>
          <BookTexField
            name='filterLanguage'
            label='Language'
            placeholder='Select language'
            value={filterLanguageId}
            onChange={(e) => {
              setFilterLanguageId(e.target.value);
              formik.setFieldValue('bookId', '');
              formik.setFieldValue('chapterId', '');
            }}
            select
            selectOptions={languageOptions}
          />
          <BookTexField
            formik={formik}
            name='bookId'
            label='Book'
            placeholder='Select book'
            select
            selectOptions={bookOptions}
          />
          <BookTexField
            formik={formik}
            name='chapterId'
            label='Chapter'
            placeholder='Select chapter'
            select
            selectOptions={chapterOptions}
          />
          <BookTexField
            formik={formik}
            name='pageNo'
            label='Page number'
            placeholder='Enter page number'
            type='number'
          />
          <BookTexField
            formik={formik}
            name='searchQuery'
            label='Search query'
            placeholder='Enter search query'
          />
        </>
      )}

      <BookTexField
        formik={formik}
        name='title'
        label='Title'
        placeholder='Enter title'
      />
      <BookTexField
        formik={formik}
        name='description'
        label='Description'
        placeholder='Enter description'
        multiline
        rows={4}
      />
      <BookTexField
        formik={formik}
        name='link'
        label='Link'
        placeholder='Enter link URL'
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          label='Scheduled At'
          value={formik.values.scheduledAt}
          onChange={(newValue) => formik.setFieldValue('scheduledAt', newValue)}
          slotProps={{
            textField: {
              fullWidth: true,
              error: formik.touched.scheduledAt && Boolean(formik.errors.scheduledAt),
              helperText: formik.touched.scheduledAt && formik.errors.scheduledAt,
              sx: { marginBottom: '1rem' },
            },
          }}
        />
      </LocalizationProvider>
      <ImageUploader
        name='url'
        imageUrl={formik.values.url}
        callBackResponse={(imageResponse) => formik.handleChange(imageResponse)}
      />

      <BookButton
        onSubmit={formik.handleSubmit}
        title={action === 'add' ? 'Add' : 'Update'}
        type='primary'
        variant='contained'
        isLoading={isLoading}
        sx={{ top: '0.625rem', marginBottom: '0.625rem' }}
      />
    </BookModal>
  );
};

export default AnnouncementModals;
